import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Space, Tag } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { ColumnsType, SortOrder } from 'antd/es/table/interface';

import { ICategory, IReward } from '../../interfaces';
import RewardsService from '../../services/http/services/RewardsService';
import CategoriesService from '../../services/http/services/CategoriesService';
import { EmitSuccessAlert } from '../../utils/EmitSuccessAlert';
import { EmitError } from '../../utils/EmitError';
import useAuth from '../../hooks/useAuth';
import { formatDateStringToDate, formatDateStringToDateAndTime } from '../../utils/Conversores';
import { removeAcento } from '../../utils/RemoveAcentos';
import { useGetColumnSearchPropsData } from '../../hooks/FilterTable/date';
import { ThemeContext } from '../../contexts/ThemeContext';
import TableActionButton from '../../components/TableActionButton';
import { IActiveFilterItem } from '../../components/ActiveFiltersBar';
import { findChunksText, HIGHLIGHT_STYLE } from '../../utils/Highlight';
import useTableColumnFilter from '../../hooks/useTableColumnFilter';
import { urlHttpGov } from '../../services/urls';

export interface ITableData extends IReward {
    key: number;
}

export interface IRewardFilters {
    name: string | null;
    category_id: number | null;
    is_inactive: boolean | null;
    created_at: string | null;
}

export type SortField =
    | 'name'
    | 'points'
    | 'stock'
    | 'vouchers_issued'
    | 'category_id'
    | 'created_at'
    | 'is_inactive'
    | null;

export default function useRewards() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const { activeCity } = useAuth();
    const navigate = useNavigate();
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    // Top-level custom search hooks
    const createdDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'created_at' });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [rewardBeingDeleted, setRewardBeingDeleted] = useState<ITableData | null>(null);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [categories, setCategories] = useState<Array<ICategory>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<IRewardFilters>({
        name: null,
        category_id: null,
        is_inactive: null,
        created_at: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'created_at',
        order: 'desc',
    });

    const [searchRewardName, setSearchRewardName] = useState(filters.name || '');

    useEffect(() => {
        setSearchRewardName(filters.name || '');
    }, [filters.name]);

    const handleFilterChange = useCallback((key: keyof IRewardFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            category_id: null,
            is_inactive: null,
            created_at: null,
        });
        setSearchRewardName('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const setTableDataFromRequest = useCallback((data: Array<IReward>) => {
        if (!Array.isArray(data)) return;
        const newData: ITableData[] = data.map((reward) => ({
            ...reward,
            key: reward.id || 0,
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        if (!activeCity) return;

        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                const [rewards, categoriesList] = await Promise.all([
                    RewardsService.listRewards(activeCity?.id!, controller.signal),
                    CategoriesService.listCategories(activeCity?.id!, controller.signal),
                ]);

                setTableDataFromRequest(rewards);
                setCategories(categoriesList || []);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'rewards_load_error');
            } finally {
                setIsLoading(false);
            }
        }

        loadData();

        return () => {
            controller.abort();
        };
    }, [activeCity, setTableDataFromRequest]);

    const filteredTableData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) =>
                item.name ? removeAcento(item.name.toLowerCase()).includes(query) : false
            );
        }

        if (filters.category_id !== null) {
            result = result.filter((item) => item.category_id === filters.category_id);
        }

        if (filters.is_inactive !== null) {
            result = result.filter((item) => Boolean(item.is_inactive) === filters.is_inactive);
        }

        if (filters.created_at) {
            const query = filters.created_at.trim();
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const formatted = formatDateStringToDate(item.created_at);
                return formatted.includes(query);
            });
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'name') {
                    const comp = (a.name || '').localeCompare(b.name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'points') {
                    const valA = a.points || 0;
                    const valB = b.points || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'stock') {
                    const valA = a.stock || 0;
                    const valB = b.stock || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'vouchers_issued') {
                    const valA = a.vouchers_issued || 0;
                    const valB = b.vouchers_issued || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'category_id') {
                    const catA = categories.find((c) => c.id === a.category_id)?.name || '';
                    const catB = categories.find((c) => c.id === b.category_id)?.name || '';
                    const comp = catA.localeCompare(catB);
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'is_inactive') {
                    const valA = a.is_inactive ? 1 : 0;
                    const valB = b.is_inactive ? 1 : 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter, categories]);

    const activeFilterItems: Array<IActiveFilterItem | null> = useMemo(() => [
        filters.name
            ? {
                key: 'name',
                label: 'Recompensa',
                value: filters.name,
                onClear: () => {
                    handleFilterChange('name', null);
                    setSearchRewardName('');
                },
            }
            : null,
        filters.category_id !== null
            ? {
                key: 'category_id',
                label: 'Categoria',
                value: categories.find((c) => c.id === filters.category_id)?.name || String(filters.category_id),
                onClear: () => handleFilterChange('category_id', null),
            }
            : null,
        filters.is_inactive !== null
            ? {
                key: 'is_inactive',
                label: 'Status',
                value: filters.is_inactive ? 'Inativas' : 'Ativas',
                onClear: () => handleFilterChange('is_inactive', null),
            }
            : null,
        filters.created_at
            ? {
                key: 'created_at',
                label: 'Data de Cadastro',
                value: formatDateStringToDate(filters.created_at),
                onClear: () => handleFilterChange('created_at', null),
            }
            : null,
    ], [filters, categories, handleFilterChange]);

    const getSortOrder = useCallback((field: SortField): SortOrder | undefined => {
        if (sorter.field === field) {
            return sorter.order === 'asc' ? 'ascend' : sorter.order === 'desc' ? 'descend' : undefined;
        }
        return undefined;
    }, [sorter]);

    const handleTableChange = (_: any, __: any, newSorter: any) => {
        if (newSorter && newSorter.field) {
            setSorter({
                field: newSorter.field as SortField,
                order: newSorter.order === 'ascend' ? 'asc' : newSorter.order === 'descend' ? 'desc' : null,
            });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const handleDeleteReward = useCallback((record: ITableData) => {
        setRewardBeingDeleted(record);
        setIsDeleteModalVisible(true);
    }, []);

    const handleConfirmDeleteReward = useCallback(async () => {
        if (!rewardBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await RewardsService.deleteReward(rewardBeingDeleted.id);
            EmitSuccessAlert('Recompensa excluída com sucesso!');
            setRawData((prev) => prev.filter((item) => item.id !== rewardBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setRewardBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, 'rewards_delete_error');
        } finally {
            setIsLoadingDelete(false);
        }
    }, [rewardBeingDeleted]);

    const handleCancelDeleteReward = useCallback(() => {
        setIsDeleteModalVisible(false);
        setRewardBeingDeleted(null);
    }, []);

    const handleClickCreateNewReward = useCallback(() => {
        navigate('/recompensas/criar');
    }, [navigate]);

    const columns: ColumnsType<ITableData> = useMemo(() => [
        {
            title: 'Recompensa',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: getSortOrder('name'),
            showSorterTooltip: false,
            ...getColumnSearchProps({
                title: 'Buscar Recompensa',
                placeholder: 'Nome do produto...',
                value: searchRewardName,
                onChange: setSearchRewardName,
                onSearch: (val) => handleFilterChange('name', val),
                isActive: Boolean(filters.name),
            }),
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: isDark ? '#141414' : '#f8fafc',
                            border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {record.image ? (
                            <img
                                src={`${urlHttpGov}/image_reward/${record.image}`}
                                alt={record.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <GiftOutlined style={{ fontSize: 18, color: isDark ? '#64748b' : '#94a3b8' }} />
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span
                            style={{
                                fontWeight: 600,
                                color: isDark ? '#f0f0f0' : '#1e293b',
                                fontSize: 13.5,
                                lineHeight: '1.3',
                            }}
                        >
                            {filters.name ? (
                                <Highlighter
                                    highlightStyle={HIGHLIGHT_STYLE}
                                    searchWords={[filters.name]}
                                    textToHighlight={record.name || '--'}
                                    findChunks={findChunksText}
                                />
                            ) : (
                                record.name || '--'
                            )}
                        </span>
                        {record.description && (
                            <span
                                style={{
                                    fontSize: 12,
                                    color: isDark ? '#94a3b8' : '#64748b',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 320,
                                }}
                                title={record.description}
                            >
                                {record.description}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Categoria',
            dataIndex: 'category_id',
            key: 'category_id',
            sorter: true,
            sortOrder: getSortOrder('category_id'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Categoria',
                value: filters.category_id !== null ? String(filters.category_id) : 'ALL',
                onChange: (val) => handleFilterChange('category_id', val === 'ALL' ? null : Number(val)),
                isActive: filters.category_id !== null,
                options: [
                    { label: 'Todas', value: 'ALL' },
                    ...categories.map((c) => ({ label: c.name, value: String(c.id) })),
                ],
            }),
            render: (categoryId: number) => {
                const cat = categories.find((c) => c.id === categoryId);
                return (
                    <span style={{ fontWeight: 500, color: isDark ? '#cbd5e1' : '#475569' }}>
                        {cat?.name || '--'}
                    </span>
                );
            },
        },
        {
            title: 'Pontos',
            dataIndex: 'points',
            key: 'points',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('points'),
            showSorterTooltip: false,
            render: (points: number) => (
                <Tag
                    color="blue"
                    style={{
                        margin: 0,
                        borderRadius: 12,
                        fontWeight: 700,
                        fontSize: 12,
                    }}
                >
                    {(points || 0).toLocaleString('pt-BR')} P
                </Tag>
            ),
        },
        {
            title: 'Estoque',
            dataIndex: 'stock',
            key: 'stock',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('stock'),
            showSorterTooltip: false,
            render: (stock: number) => {
                const isZero = stock === 0;
                return (
                    <span
                        style={{
                            fontWeight: 600,
                            color: isZero ? '#ef4444' : isDark ? '#f1f5f9' : '#1e293b',
                        }}
                    >
                        {(stock || 0).toLocaleString('pt-BR')}
                    </span>
                );
            },
        },
        {
            title: 'Pendentes',
            dataIndex: 'vouchers_issued',
            key: 'vouchers_issued',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('vouchers_issued'),
            showSorterTooltip: false,
            render: (issued: number) => (
                <span
                    style={{
                        fontWeight: (issued || 0) > 0 ? 700 : 400,
                        color: (issued || 0) > 0 ? '#fa8c16' : isDark ? '#94a3b8' : '#64748b',
                    }}
                >
                    {(issued || 0).toLocaleString('pt-BR')}
                </span>
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            ...createdDateSearchProps,
            render: (date: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {date ? formatDateStringToDateAndTime(date) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'is_inactive',
            key: 'is_inactive',
            sorter: true,
            sortOrder: getSortOrder('is_inactive'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Status',
                value: filters.is_inactive !== null ? (filters.is_inactive ? 'INACTIVE' : 'ACTIVE') : 'ALL',
                onChange: (val) =>
                    handleFilterChange(
                        'is_inactive',
                        val === 'ALL' ? null : val === 'INACTIVE' ? true : false
                    ),
                isActive: filters.is_inactive !== null,
                options: [
                    { label: 'Todos', value: 'ALL' },
                    { label: 'Ativas', value: 'ACTIVE' },
                    { label: 'Inativas', value: 'INACTIVE' },
                ],
            }),
            render: (isInactive: boolean) => (
                <Tag
                    color={!isInactive ? 'success' : 'error'}
                    style={{
                        borderRadius: 6,
                        fontWeight: 600,
                        padding: '2px 8px',
                    }}
                >
                    {!isInactive ? 'Ativa' : 'Inativa'}
                </Tag>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 100,
            render: (_: any, record: ITableData) => (
                <Space size={4}>
                    <TableActionButton
                        action="edit"
                        tooltip="Editar recompensa"
                        onClick={() => navigate(`/recompensas/editar/${record.id}`)}
                    />
                    <TableActionButton
                        action="delete"
                        tooltip="Excluir recompensa"
                        onClick={() => handleDeleteReward(record)}
                    />
                </Space>
            ),
        },
    ], [
        isDark,
        filters,
        categories,
        getSortOrder,
        searchRewardName,
        getColumnSearchProps,
        getColumnRadioProps,
        createdDateSearchProps,
        handleFilterChange,
        handleDeleteReward,
        navigate,
    ]);

    return {
        isLoading,
        isLoadingDelete,
        isDeleteModalVisible,
        rewardBeingDeleted,
        columns,
        tableData: filteredTableData,
        pagination,
        activeFilterItems,
        handleClickCreateNewReward,
        handleConfirmDeleteReward,
        handleCancelDeleteReward,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
